/**
 * useWorkflowOrdering -- the FI admin's view of which workflow ordering
 * governs pledges on their own tenant, and which alternatives (if any) an
 * Aaim operator has made available to them.
 *
 * Ruling D3 (2026-08-17), reaffirming ruling 3 of 2026-08-13: selecting an
 * ordering is an FI act; curating the catalog and deciding what a tenant may
 * choose from is an Aaim-operator act on a different host. This hook covers
 * the FI half only -- there is no allowlist write anywhere in it.
 *
 * TENANT RESOLUTION. tenantId comes from the authenticated session claim in
 * the auth store, exactly as useCollateralPolicy does, and never from a route
 * param. The server re-derives it from the JWT via resolveEffectiveTenantId
 * and 404s a caller reaching for a tenant it does not own, so the claim here
 * is a cache key and a path argument, never the authority.
 *
 * WHAT IS FETCHED, and why three reads rather than one:
 *
 *   binding    the tenant's selection, or null when nothing is selected
 *   allowlist  the recipe families this tenant may bind to, possibly empty
 *   families   one read per family, for the STEPS behind each id
 *
 * The third read is what makes the choice legible. The allowlist returns bare
 * recipe ids; an id tells an FI admin nothing about what selecting it would
 * do to their pledge flow. The families carry the ordered gates.
 *
 * The family set is the UNION of the allowlisted ids and the currently bound
 * id. Those can differ: an operator may remove a family from the allowlist
 * after the FI bound it, and the binding survives that -- so the ordering in
 * effect would otherwise render as an id with no detail.
 */

import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../stores/auth';
import {
  workflowRecipesApi,
  type RecipeMutationResult,
  type TenantRecipeBinding,
  type WorkflowRecipe,
  type WorkflowRecipeFamily,
} from '../api/services/workflow-recipes';

export type {
  RecipeActor,
  RecipeGate,
  RecipeStep,
  RecipeStatus,
  RecipeValidationError,
  TenantRecipeBinding,
  WorkflowRecipe,
} from '../api/services/workflow-recipes';

const BINDING_KEY = 'workflow-recipe-binding';
const ALLOWLIST_KEY = 'workflow-recipe-allowlist';
const FAMILIES_KEY = 'workflow-recipe-families';

/** The published version of one allowlisted family, ready to render or select. */
function publishedVersionOf(family: WorkflowRecipeFamily | null): WorkflowRecipe | null {
  if (!family || family.publishedVersion == null) return null;
  return family.versions.find((v) => v.version === family.publishedVersion) ?? null;
}

export function useWorkflowOrdering() {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const tenantId = user?.tenantId ?? '';

  const bindingQuery = useQuery({
    queryKey: [BINDING_KEY, tenantId],
    queryFn: () => workflowRecipesApi.getBinding(tenantId),
    enabled: !!tenantId,
  });

  const allowlistQuery = useQuery({
    queryKey: [ALLOWLIST_KEY, tenantId],
    queryFn: () => workflowRecipesApi.getAllowlist(tenantId),
    enabled: !!tenantId,
  });

  const allowlistedIds: string[] = useMemo(
    () => allowlistQuery.data?.recipeIds ?? [],
    [allowlistQuery.data],
  );
  const boundRecipeId: string | null = bindingQuery.data?.recipeId ?? null;

  // Union, allowlist order first so the options render in the order the
  // operator granted them. The bound id is appended only when it is no longer
  // allowlisted -- otherwise it is already in the list.
  const familyIds: string[] = useMemo(() => {
    const ids = [...allowlistedIds];
    if (boundRecipeId && !ids.includes(boundRecipeId)) ids.push(boundRecipeId);
    return ids;
  }, [allowlistedIds, boundRecipeId]);

  // One query over the whole set rather than one per id: the set is small (an
  // allowlist is a handful of families at most), and a single key keeps the
  // loading state a single boolean the page can reason about.
  const familiesQuery = useQuery({
    queryKey: [FAMILIES_KEY, tenantId, familyIds.join(',')],
    queryFn: () => Promise.all(familyIds.map((id) => workflowRecipesApi.getFamily(id))),
    enabled: !!tenantId && familyIds.length > 0,
  });

  const familiesById = useMemo(() => {
    const map = new Map<string, WorkflowRecipeFamily>();
    for (const family of familiesQuery.data ?? []) {
      if (family) map.set(family.recipeId, family);
    }
    return map;
  }, [familiesQuery.data]);

  /**
   * The ordering in effect, resolved to the exact pinned version. Null when
   * the tenant has selected nothing -- which is the platform-default state,
   * not an error.
   *
   * `recipe` can be null while `binding` is not: the bound version may have
   * been retired, or the family read may have failed. The page must not
   * silently drop the binding in that case, so both are returned separately.
   */
  const selectedRecipe: WorkflowRecipe | null = useMemo(() => {
    const binding = bindingQuery.data;
    if (!binding) return null;
    const family = familiesById.get(binding.recipeId);
    return family?.versions.find((v) => v.version === binding.recipeVersion) ?? null;
  }, [bindingQuery.data, familiesById]);

  /**
   * The published versions the FI admin may select, in allowlist order. A
   * family with no published version is omitted: the server refuses a binding
   * to a DRAFT or RETIRED version (V6_BINDING_REQUIRES_PUBLISHED), so offering
   * one would be a control the server would reject.
   */
  const options: WorkflowRecipe[] = useMemo(
    () =>
      allowlistedIds
        .map((id) => publishedVersionOf(familiesById.get(id) ?? null))
        .filter((r): r is WorkflowRecipe => r !== null),
    [allowlistedIds, familiesById],
  );

  const select = useMutation<RecipeMutationResult<TenantRecipeBinding>, Error, WorkflowRecipe>({
    mutationFn: (recipe) => {
      if (!tenantId) {
        throw new Error('Cannot select an ordering without an active tenant context');
      }
      return workflowRecipesApi.putBinding(tenantId, recipe.recipeId, recipe.version);
    },
    onSuccess: (result) => {
      // A 422 resolves rather than rejects, so only a genuine write invalidates.
      // The binding is refetched rather than written from the response because
      // the response omits the consumer-mirror parity signal by design -- its
      // push is still in flight at that moment, and reporting the pre-write
      // mirror state would be worse than reporting nothing.
      if (!result.ok) return;
      void queryClient.invalidateQueries({ queryKey: [BINDING_KEY, tenantId] });
    },
  });

  return {
    tenantId,
    binding: bindingQuery.data ?? null,
    selectedRecipe,
    options,
    isLoading: bindingQuery.isLoading || allowlistQuery.isLoading || familiesQuery.isLoading,
    loadError: bindingQuery.error ?? allowlistQuery.error ?? familiesQuery.error,
    select,
  };
}
